import type { MeliPublishValidationIssue } from 'src/api/generated';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';

interface IssueSectionRule {
  prefix: string;
  sectionKey: TranslationKey;
}

export interface MeliDraftIssueSection {
  sectionKey: TranslationKey;
  issues: MeliPublishValidationIssue[];
}

const ISSUE_FIELD_LABEL_KEY_BY_FIELD: Record<string, TranslationKey> = {
  'property.customFields.landAccess': 'catalog.properties.form.landAccess',
  'property.name': 'catalog.properties.form.name',
  'property.description': 'catalog.properties.form.description',
  'property.propertyType': 'catalog.properties.form.propertyType',
  'property.propertySubtype': 'catalog.properties.form.propertyType',
  'property.condition': 'catalog.properties.form.condition',
  'property.operation': 'catalog.properties.form.operation',
  'property.isActive': 'catalog.properties.form.isActive',
  operation: 'catalog.properties.form.operation',
  'property.salePriceAmount': 'catalog.properties.form.salePriceAmount',
  'property.salePriceCurrency': 'catalog.properties.form.salePriceCurrency',
  'property.rentPriceAmount': 'catalog.properties.form.rentPriceAmount',
  'property.rentPriceCurrency': 'catalog.properties.form.rentPriceCurrency',
  'property.bedrooms': 'catalog.properties.form.bedrooms',
  'property.bathrooms': 'catalog.properties.form.bathrooms',
  'property.parkingLots': 'catalog.properties.form.parkingLots',
  'property.warehouses': 'catalog.properties.form.warehouses',
  'property.rooms': 'catalog.properties.form.rooms',
  'property.unitFloor': 'catalog.properties.form.unitFloor',
  'property.floors': 'catalog.properties.form.floors',
  'property.apartmentsPerFloor': 'catalog.properties.form.apartmentsPerFloor',
  'property.propertyAge': 'catalog.properties.form.propertyAge',
  'property.orientation': 'catalog.properties.form.orientation',
  'property.unit': 'catalog.properties.form.unit',
  'property.cadastralId': 'catalog.properties.form.cadastralId',
  'property.areaUsable': 'catalog.properties.form.surfaceUsable',
  'property.areaBuilt': 'catalog.properties.form.surfaceCovered',
  'property.areaTotal': 'catalog.properties.form.surfaceTotal',
  'property.areaOutdoor': 'catalog.properties.form.surfaceOutdoor',
  'property.commonExpensesAmount':
    'catalog.properties.form.commonExpensesAmount',
  'location.addressLine': 'catalog.properties.form.addressDisplay',
  'location.neighborhoodId': 'catalog.properties.form.neighborhood',
  'location.latitude': 'catalog.properties.form.latitude',
  'location.longitude': 'catalog.properties.form.longitude',
  location: 'catalog.properties.editor.sections.location',
  listingTypeId: 'catalog.properties.meli.form.listingType',
  sellerContact: 'catalog.properties.meli.validation.field.sellerContact',
  pictures: 'catalog.properties.meli.images.title',
  'property.customFields.furnished':
    'catalog.properties.form.feature.furnished',
  'property.customFields.petsAllowed':
    'catalog.properties.form.feature.petsAllowed',
  'property.customFields.hasVirtualTour':
    'catalog.properties.form.feature.hasVirtualTour',
  'property.customFields.hasBalcony':
    'catalog.properties.form.feature.hasBalcony',
  'property.customFields.hasKitchen':
    'catalog.properties.form.feature.hasKitchen',
  'property.customFields.hasLift': 'catalog.properties.form.feature.hasLift',
  'property.customFields.hasPlayground':
    'catalog.properties.form.feature.hasPlayground',
  'property.customFields.hasRoofGarden':
    'catalog.properties.form.feature.hasRoofGarden',
  'property.customFields.hasBusinessCenter':
    'catalog.properties.form.feature.hasBusinessCenter',
  'property.customFields.hasCinemaHall':
    'catalog.properties.form.feature.hasCinemaHall',
  'property.customFields.hasTennisCourt':
    'catalog.properties.form.feature.hasTennisCourt',
  'property.customFields.hasGarden':
    'catalog.properties.form.feature.hasGarden',
  'property.customFields.hasPatio': 'catalog.properties.form.feature.hasPatio',
  'property.customFields.hasTerrace':
    'catalog.properties.form.feature.hasTerrace',
  'property.customFields.hasLaundry':
    'catalog.properties.form.feature.hasLaundry',
  'property.customFields.hasPool': 'catalog.properties.form.feature.hasPool',
  'property.customFields.hasGym': 'catalog.properties.form.feature.hasGym',
  'property.customFields.hasSecurity':
    'catalog.properties.form.feature.hasSecurity',
  'property.customFields.hasHeating':
    'catalog.properties.form.feature.hasHeating',
  'property.customFields.hasAirConditioning':
    'catalog.properties.form.feature.hasAirConditioning',
  'property.customFields.hasInternetAccess':
    'catalog.properties.form.feature.hasInternetAccess',
};

const ISSUE_MESSAGE_KEY_BY_CODE: Partial<
  Record<MeliPublishValidationIssue['code'], TranslationKey>
> = {
  missing_title: 'catalog.properties.meli.validation.issue.missing_title',
  missing_description:
    'catalog.properties.meli.validation.issue.missing_description',
  missing_property_type:
    'catalog.properties.meli.validation.issue.missing_property_type',
  missing_condition:
    'catalog.properties.meli.validation.issue.missing_condition',
  missing_operation:
    'catalog.properties.meli.validation.issue.missing_operation',
  missing_address_line:
    'catalog.properties.meli.validation.issue.missing_address_line',
  missing_location_node:
    'catalog.properties.meli.validation.issue.missing_location_node',
  missing_latitude: 'catalog.properties.meli.validation.issue.missing_latitude',
  missing_longitude:
    'catalog.properties.meli.validation.issue.missing_longitude',
  missing_seller_contact:
    'catalog.properties.meli.validation.issue.missing_seller_contact',
  missing_pictures: 'catalog.properties.meli.validation.issue.missing_pictures',
  inactive_property:
    'catalog.properties.meli.validation.issue.inactive_property',
  unsupported_external_media:
    'catalog.properties.meli.validation.issue.unsupported_external_media',
};

const ISSUE_SECTION_RULES: IssueSectionRule[] = [
  {
    prefix: 'property.externalMedia',
    sectionKey: 'catalog.properties.editor.sections.media',
  },
  {
    prefix: 'property.name',
    sectionKey: 'catalog.properties.editor.sections.overview',
  },
  {
    prefix: 'property.description',
    sectionKey: 'catalog.properties.editor.sections.overview',
  },
  {
    prefix: 'property.propertyType',
    sectionKey: 'catalog.properties.editor.sections.overview',
  },
  {
    prefix: 'property.operation',
    sectionKey: 'catalog.properties.editor.sections.overview',
  },
  {
    prefix: 'operation',
    sectionKey: 'catalog.properties.editor.sections.overview',
  },
  {
    prefix: 'property.isActive',
    sectionKey: 'catalog.properties.editor.sections.overview',
  },
  {
    prefix: 'property.salePrice',
    sectionKey: 'catalog.properties.editor.sections.pricing',
  },
  {
    prefix: 'property.rentPrice',
    sectionKey: 'catalog.properties.editor.sections.pricing',
  },
  {
    prefix: 'property.commonExpensesAmount',
    sectionKey: 'catalog.properties.editor.sections.pricing',
  },
  {
    prefix: 'property.customFields.landAccess',
    sectionKey: 'catalog.properties.editor.sections.characteristics',
  },
  {
    prefix: 'property.bedrooms',
    sectionKey: 'catalog.properties.editor.sections.characteristics',
  },
  {
    prefix: 'property.bathrooms',
    sectionKey: 'catalog.properties.editor.sections.characteristics',
  },
  {
    prefix: 'property.area',
    sectionKey: 'catalog.properties.editor.sections.characteristics',
  },
  {
    prefix: 'attributes.',
    sectionKey: 'catalog.properties.editor.sections.characteristics',
  },
  {
    prefix: 'location',
    sectionKey: 'catalog.properties.editor.sections.location',
  },
  {
    prefix: 'pictures',
    sectionKey: 'catalog.properties.editor.sections.media',
  },
  {
    prefix: 'categoryId',
    sectionKey: 'catalog.properties.editor.sections.publishing',
  },
  {
    prefix: 'listingTypeId',
    sectionKey: 'catalog.properties.editor.sections.publishing',
  },
  {
    prefix: 'siteId',
    sectionKey: 'catalog.properties.editor.sections.publishing',
  },
  {
    prefix: 'sellerContact',
    sectionKey: 'catalog.properties.editor.sections.publishing',
  },
];

const getIssueSectionKey = (field: string): TranslationKey => {
  const matchingRule = ISSUE_SECTION_RULES.find((rule) =>
    field.startsWith(rule.prefix)
  );

  return (
    matchingRule?.sectionKey ?? 'catalog.properties.editor.sections.publishing'
  );
};

export const groupMeliDraftIssuesBySection = (
  issues: MeliPublishValidationIssue[]
): MeliDraftIssueSection[] => {
  const groupedIssues = new Map<TranslationKey, MeliPublishValidationIssue[]>();

  for (const issue of issues) {
    const sectionKey = getIssueSectionKey(issue.field);
    const sectionIssues = groupedIssues.get(sectionKey) ?? [];
    sectionIssues.push(issue);
    groupedIssues.set(sectionKey, sectionIssues);
  }

  return Array.from(groupedIssues.entries()).map(
    ([sectionKey, sectionIssues]) => ({
      sectionKey,
      issues: sectionIssues,
    })
  );
};

export const getMeliDraftIssueFieldLabelKey = (
  field: string
): TranslationKey | null =>
  field.startsWith('property.externalMedia.')
    ? 'catalog.properties.form.externalMedia.title'
    : ISSUE_FIELD_LABEL_KEY_BY_FIELD[field] ?? null;

const getMissingPriceIssueMessageKey = (
  issue: MeliPublishValidationIssue
): TranslationKey =>
  issue.field === 'property.rentPriceAmount'
    ? 'catalog.properties.meli.validation.issue.missing_rent_price'
    : 'catalog.properties.meli.validation.issue.missing_sale_price';

const getMissingCurrencyIssueMessageKey = (
  issue: MeliPublishValidationIssue
): TranslationKey =>
  issue.field === 'property.rentPriceCurrency'
    ? 'catalog.properties.meli.validation.issue.missing_rent_currency'
    : 'catalog.properties.meli.validation.issue.missing_sale_currency';

const isResolvedLocationIssue = (issue: MeliPublishValidationIssue): boolean =>
  issue.field === 'location' ||
  issue.message ===
    'Resolved MercadoLibre location ids and coordinates are required before publishing.';

export const getMeliDraftIssueMessageKey = (
  issue: MeliPublishValidationIssue
): TranslationKey | null => {
  if (issue.code === 'missing_price') {
    return getMissingPriceIssueMessageKey(issue);
  }

  if (issue.code === 'missing_currency') {
    return getMissingCurrencyIssueMessageKey(issue);
  }

  if (isResolvedLocationIssue(issue)) {
    return 'catalog.properties.meli.validation.issue.resolved_location_required';
  }

  if (issue.code === 'missing_category_required_attribute') {
    return getMeliDraftIssueFieldLabelKey(issue.field)
      ? 'catalog.properties.meli.validation.issue.missing_category_required_attribute'
      : null;
  }

  return ISSUE_MESSAGE_KEY_BY_CODE[issue.code] ?? null;
};

/**
 * Builds interpolation parameters for a localized MercadoLibre draft issue.
 *
 * @param issue - Draft validation issue being translated.
 * @param fieldLabel - Localized label for the issue's property field.
 * @returns Field interpolation parameters for missing category attributes, otherwise undefined.
 */
export const getMeliDraftIssueMessageParams = (
  issue: MeliPublishValidationIssue,
  fieldLabel: string
): Record<string, string> | undefined => {
  if (issue.code !== 'missing_category_required_attribute') {
    return undefined;
  }

  return {
    field: fieldLabel || issue.field,
  };
};
